/* eslint-disable max-lines-per-function, indent, @typescript-eslint/no-explicit-any */
import { TechnicianRoute } from '../types';
import { getManualStopReasonLabel } from '@/lib/validation/manualStopValidation';
import { formatUsPhone } from '@/lib/manualStops/formatUsPhone';
import { CUSTOMER_STOP_LABEL } from '@/lib/manualStops/locationKey';
import type { ManualStopContact } from '@/types/manualStops';

/** Customer contacts for the stops being printed, keyed by contact_id (fetched at print time). */
export type PrintContacts = Record<string, ManualStopContact>;

type PrintStop = TechnicianRoute['stops'][0];

const isCustomerPrintStop = (stop: PrintStop): boolean =>
  stop.stop_kind === 'customer' || (stop.recalls ?? []).some(r => r.stop_kind === 'customer');

const printStopContactId = (stop: PrintStop): string | undefined =>
  stop.contact_id || (stop.recalls ?? []).find(r => r.contact_id)?.contact_id || undefined;

/** Unique dispatcher notes across a set of stops, in order. */
const dispatcherNotesFor = (stops: PrintStop[]): string[] =>
  Array.from(
    new Set(
      stops
        .flatMap(s => (s.recalls ?? []).map(r => r.dispatcher_note?.trim()))
        .filter((n): n is string => !!n)
    )
  );

/**
 * Customer name + phone for a printout line, already HTML-escaped. Technicians need both
 * to call ahead and greet the customer. Email is left off paper on purpose.
 */
function customerContactText(contactId: string | undefined, contacts: PrintContacts): string {
  if (!contactId) return '';
  const contact = contacts[contactId];
  if (!contact) return 'Contact available to dispatchers';
  if (contact.purged) return 'Customer details removed';
  const name = [contact.first_name, contact.last_name].filter(Boolean).join(' ');
  const phone = contact.phone_e164 ? formatUsPhone(contact.phone_e164) : '';
  return [esc(name), esc(phone)].filter(Boolean).join(' · ');
}

/** All customer contact ids in the routes being printed. */
export function collectPrintContactIds(routes: TechnicianRoute[]): string[] {
  const ids = routes.flatMap(r => r.stops.filter(isCustomerPrintStop).map(printStopContactId));
  return Array.from(new Set(ids.filter((id): id is string => !!id)));
}

/**
 * Fetch customer contacts for printing (logged as context 'print'). Never throws: if the
 * person printing may not see them, the sheet prints "Contact available to dispatchers".
 */
async function fetchContactsForPrint(routes: TechnicianRoute[]): Promise<PrintContacts> {
  const ids = collectPrintContactIds(routes);
  if (ids.length === 0) return {};
  try {
    const { getManualStopContacts } = await import('@/lib/supabase/manualStopContacts');
    const rows = await getManualStopContacts(ids, 'print');
    return Object.fromEntries(rows.map(row => [row.contact_id, row]));
  } catch (err) {
    // eslint-disable-next-line no-console -- best-effort: print must not fail on contacts
    console.warn('Customer contacts unavailable for print:', err);
    return {};
  }
}

interface DealerGroup {
  dealer: TechnicianRoute['stops'][0]['dealer'];
  stops: TechnicianRoute['stops'];
  /** A customer's home (one household per group) */
  isCustomer?: boolean;
  contactId?: string | undefined;
  addressLine2?: string | null | undefined;
}

// Notes are dispatcher free-form input — escape before HTML injection.
const HTML_ESCAPE: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
};
const escapeHTML = (s: string): string => s.replace(/[&<>"']/g, c => HTML_ESCAPE[c] ?? c);

// Every value interpolated into print HTML comes from user-entered or synced data
// (dealer names, addresses, VINs, reasons, notes). Escape all of it — the print
// window is written with document.write, so unescaped text is an XSS sink.
const esc = (v: unknown): string => (v === null || v === undefined ? '' : escapeHTML(String(v)));

// For values placed inside a CSS string literal (e.g. @page `content: "..."`).
// HTML entities are not decoded inside <style>, so strip characters that could
// close the string or the style element instead.
const cssStringEscape = (v: unknown): string =>
  v === null || v === undefined ? '' : String(v).replace(/["\\<>\r\n]/g, '');

// Renders the per-stop notes cell for the Route Summary table. Em-dash fallback
// for stops without notes — matches the convention used elsewhere for missing
// phone / address values.
const renderSummaryNotesCell = (
  stop: TechnicianRoute['stops'][0],
  dealerNotes: Record<string, string>
): string => {
  // Per-dealer notes are keyed by dealer name — every customer stop is 'Customer stop',
  // so they never apply to customers. The dispatcher's note on the stop always does.
  const dealerName = isCustomerPrintStop(stop) ? undefined : stop.dealer?.name;
  const notes = [dealerName ? dealerNotes[dealerName] : undefined, ...dispatcherNotesFor([stop])]
    .filter((n): n is string => !!n)
    .map(escapeHTML);
  return notes.length > 0 ? notes.join('<br />') : '<span class="notes-cell-empty">—</span>';
};

// Batch-fetch per-dealer technician_notes (the orange-field notes set in the
// Simple Recall Map dealer detail panel) for every dealer in the route.
// Returns a Record<dealerName, technician_notes>. Empty record on auth/data failure.
async function fetchTechnicianNotesForRoute(
  route: TechnicianRoute
): Promise<Record<string, string>> {
  const dealerNames = Array.from(
    new Set(
      route.stops
        .filter(s => !isCustomerPrintStop(s))
        .map(s => s.dealer?.name)
        .filter((n): n is string => !!n)
    )
  );
  if (dealerNames.length === 0) return {};

  try {
    const { supabase } = await import('@/lib/supabase/client');
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData.session?.user?.id;
    if (!userId) return {};

    const { preferencesAPI } = await import('@/lib/supabase/preferences');
    const notesData = await preferencesAPI.getDealerNotesBatch(userId, dealerNames);

    const out: Record<string, string> = {};
    for (const [name, n] of Object.entries(notesData)) {
      if (n?.technician_notes) out[name] = n.technician_notes;
    }
    return out;
  } catch (err) {
    // eslint-disable-next-line no-console -- best-effort: print must not fail on notes lookup
    console.warn('Failed to fetch dealer technician notes for print:', err);
    return {};
  }
}

// Fetch notes for many routes in a single batched RPC.
async function fetchTechnicianNotesForRoutes(
  routes: TechnicianRoute[]
): Promise<Record<string, string>> {
  const allNames = routes.flatMap(r =>
    r.stops.filter(s => !isCustomerPrintStop(s)).map(s => s.dealer?.name)
  );
  const dealerNames = Array.from(new Set(allNames.filter((n): n is string => !!n)));
  if (dealerNames.length === 0) return {};

  try {
    const { supabase } = await import('@/lib/supabase/client');
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData.session?.user?.id;
    if (!userId) return {};

    const { preferencesAPI } = await import('@/lib/supabase/preferences');
    const notesData = await preferencesAPI.getDealerNotesBatch(userId, dealerNames);

    const out: Record<string, string> = {};
    for (const [name, n] of Object.entries(notesData)) {
      if (n?.technician_notes) out[name] = n.technician_notes;
    }
    return out;
  } catch (err) {
    // eslint-disable-next-line no-console -- best-effort: print must not fail on notes lookup
    console.warn('Failed to fetch dealer technician notes for multi-route print:', err);
    return {};
  }
}

interface RecallData {
  vin: string;
  car_year?: string | number;
  make_name?: string;
  model_name?: string;
  campaign_number?: string;
  recall_title?: string;
  dealer_street?: string;
  dealer_address?: string;
  dealer_city?: string;
  dealer_region?: string;
  dealer_postal_code?: string;
  stock_number?: string;
  // Manual stop support
  stop_type?: 'recall' | 'manual';
  manual_stop_reason?: string;
  manual_stop_notes?: string;
  // Manual stop vehicle info (different field names from recalls)
  vehicle_year?: number | null;
  vehicle_make?: string;
  vehicle_model?: string;
}

// Helper to analyze stop content for mixed stop detection
interface StopAnalysis {
  hasManualStop: boolean;
  hasRecallStop: boolean;
  isMixedStop: boolean;
  isPureManualStop: boolean;
  manualStopReason: string | undefined;
  manualStopVin: string | undefined;
  manualStopCampaign: string | undefined;
  recallCount: number;
}

function analyzeStop(stop: TechnicianRoute['stops'][0]): StopAnalysis {
  const recalls = (stop.recalls || []) as RecallData[];
  const hasManualStop = stop.stop_type === 'manual' || recalls.some(r => r.stop_type === 'manual');
  const recallOnlyItems = recalls.filter(r => !r.stop_type || r.stop_type === 'recall');
  const hasRecallStop = recallOnlyItems.length > 0;

  const manualRecall = recalls.find(r => r.stop_type === 'manual');
  const uniqueRecalls = new Set(recallOnlyItems.map(r => `${r.vin}-${r.campaign_number}`));

  return {
    hasManualStop,
    hasRecallStop,
    isMixedStop: hasManualStop && hasRecallStop,
    isPureManualStop: hasManualStop && !hasRecallStop,
    manualStopReason: manualRecall?.manual_stop_reason,
    manualStopVin: manualRecall?.vin,
    manualStopCampaign: manualRecall?.campaign_number,
    recallCount: uniqueRecalls.size
  };
}

// Info for a single manual stop
interface ManualStopInfo {
  reason: string | undefined;
  vin: string | undefined;
  campaign: string | undefined;
  recallTitle: string | undefined;
  year: string | undefined;
  make: string | undefined;
  model: string | undefined;
  notes: string | undefined;
}

// Helper to analyze a dealer group for mixed content detection
interface DealerGroupAnalysis {
  hasManualStop: boolean;
  hasRecallStop: boolean;
  isMixedStop: boolean;
  isPureManualStop: boolean;
  // Array of ALL manual stops at this dealer
  manualStops: ManualStopInfo[];
  // Keep first stop fields for backwards compatibility
  manualStopReason: string | undefined;
  manualStopVin: string | undefined;
  manualStopCampaign: string | undefined;
  manualStopYear: string | undefined;
  manualStopMake: string | undefined;
  manualStopModel: string | undefined;
  manualStopNotes: string | undefined;
}

function analyzeDealerGroup(dealerGroup: DealerGroup): DealerGroupAnalysis {
  let hasManualStop = false;
  let hasRecallStop = false;
  const manualStops: ManualStopInfo[] = [];

  for (const stop of dealerGroup.stops) {
    const recalls = (stop.recalls || []) as RecallData[];

    // Check for manual stops - collect ALL of them
    if (stop.stop_type === 'manual' || recalls.some(r => r.stop_type === 'manual')) {
      hasManualStop = true;
      const manualRecalls = recalls.filter(r => r.stop_type === 'manual');
      manualRecalls.forEach(manualRecall => {
        manualStops.push({
          reason: manualRecall.manual_stop_reason,
          vin: manualRecall.vin,
          campaign: manualRecall.campaign_number,
          // Dispatcher-entered recall description (from the campaign association)
          recallTitle: manualRecall.recall_title,
          // Manual stops use vehicle_year/make/model, recalls use car_year/make_name/model_name
          year: (manualRecall.vehicle_year ?? manualRecall.car_year)?.toString(),
          make: manualRecall.vehicle_make || manualRecall.make_name,
          model: manualRecall.vehicle_model || manualRecall.model_name,
          notes: manualRecall.manual_stop_notes
        });
      });
    }

    // Check for recall stops
    const recallOnlyItems = recalls.filter(r => !r.stop_type || r.stop_type === 'recall');
    if (recallOnlyItems.length > 0) {
      hasRecallStop = true;
    }
  }

  // For backwards compatibility, keep first stop fields populated
  const firstManualStop = manualStops[0];

  return {
    hasManualStop,
    hasRecallStop,
    isMixedStop: hasManualStop && hasRecallStop,
    isPureManualStop: hasManualStop && !hasRecallStop,
    manualStops,
    manualStopReason: firstManualStop?.reason,
    manualStopVin: firstManualStop?.vin,
    manualStopCampaign: firstManualStop?.campaign,
    manualStopYear: firstManualStop?.year,
    manualStopMake: firstManualStop?.make,
    manualStopModel: firstManualStop?.model,
    manualStopNotes: firstManualStop?.notes
  };
}

export interface RouteSheetData {
  technician: {
    name: string;
    phone: string;
    email: string;
  };
  route: {
    totalStops: number;
    totalDistance: number;
    totalDuration: number;
    estimatedStartTime: string;
    estimatedEndTime: string;
  };
  stops: Array<{
    stopNumber: number;
    dealerName: string;
    address: string;
    phone: string;
    arrivalTime: string;
    serviceTime: number;
    recalls: Array<{
      vin: string;
      campaignNumber: string;
      title: string;
    }>;
  }>;
}

export const formatRouteForPrint = (route: TechnicianRoute): RouteSheetData => {
  const sortedStops = [...route.stops].sort((a, b) => a.stop_order - b.stop_order);

  return {
    technician: {
      name: route.technician.full_name,
      phone: route.technician.phone_number,
      email: route.technician.email
    },
    route: {
      totalStops: route.stops.length,
      totalDistance: route.total_distance,
      totalDuration: route.total_duration,
      estimatedStartTime: sortedStops[0]?.arrival_time || '',
      estimatedEndTime: sortedStops[sortedStops.length - 1]?.departure_time || ''
    },
    stops: sortedStops.map((stop, index) => ({
      stopNumber: index + 1,
      dealerName: stop.dealer.name,
      address: [
        stop.dealer.address,
        stop.dealer.city,
        `${stop.dealer.region} ${stop.dealer.postal_code}`
      ].join(', '),
      phone: stop.dealer.phone || (stop as any).dealer_phone || '',
      arrivalTime: new Date(stop.arrival_time).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }),
      serviceTime: stop.service_duration,
      recalls: (stop.recalls || []).map((recall: RecallData) => ({
        vin: recall.vin || 'N/A',
        campaignNumber: recall.campaign_number || 'N/A',
        title: recall.recall_title || 'Recall information not available'
      }))
    }))
  };
};

export const generateRouteSheetHTML = (routeData: RouteSheetData): string => {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Route Sheet - ${esc(routeData.technician.name)}</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          margin: 20px;
          color: #333;
        }
        .header {
          border-bottom: 2px solid #3b82f6;
          padding-bottom: 20px;
          margin-bottom: 30px;
        }
        .company-logo {
          font-size: 24px;
          font-weight: bold;
          color: #3b82f6;
          margin-bottom: 10px;
        }
        .route-title {
          font-size: 20px;
          font-weight: bold;
          margin-bottom: 15px;
        }
        .technician-info {
          display: flex;
          gap: 30px;
          margin-bottom: 20px;
        }
        .info-section {
          background-color: #f8fafc;
          padding: 15px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        .info-section h3 {
          margin-top: 0;
          color: #1e40af;
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 5px;
        }
        .stops-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
        }
        .stops-table th,
        .stops-table td {
          border: 1px solid #e2e8f0;
          padding: 12px;
          text-align: left;
        }
        .stops-table th {
          background-color: #f1f5f9;
          font-weight: bold;
          color: #1e40af;
        }
        .stops-table tr:nth-child(even) {
          background-color: #f8fafc;
        }
        .stop-number {
          background-color: #3b82f6;
          color: white;
          width: 25px;
          height: 25px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 12px;
        }
        .recalls-list {
          font-size: 12px;
          color: #6b7280;
        }
        .footer {
          margin-top: 30px;
          padding-top: 20px;
          border-top: 1px solid #e2e8f0;
          font-size: 12px;
          color: #6b7280;
        }
        @media print {
          body { margin: 0; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="company-logo">Vehix</div>
        <div class="route-title">Daily Route Sheet</div>
        <div class="technician-info">
          <div><strong>Technician:</strong> ${esc(routeData.technician.name)}</div>
          <div><strong>Phone:</strong> ${esc(routeData.technician.phone)}</div>
          <div><strong>Date:</strong> ${new Date().toLocaleDateString()}</div>
        </div>
      </div>

      <div class="info-section">
        <h3>Route Summary</h3>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px;">
          <div><strong>Total Stops:</strong> ${routeData.route.totalStops}</div>
          <div>
            <strong>Estimated Distance:</strong>
            ${routeData.route.totalDistance.toFixed(1)} miles
          </div>
          <div>
            <strong>Estimated Duration:</strong>
            ${Math.round(routeData.route.totalDuration / 60)} hours
          </div>
          <div><strong>Start Time:</strong> ${esc(routeData.route.estimatedStartTime)}</div>
        </div>
      </div>

      <div class="info-section">
        <h3>Stop Details</h3>
        <table class="stops-table">
          <thead>
            <tr>
              <th>Stop</th>
              <th>Dealer</th>
              <th>Address</th>
              <th>Phone</th>
              <th>Arrival</th>
              <th>Service Time</th>
            </tr>
          </thead>
          <tbody>
            ${routeData.stops
              .map(
                stop => `
              <tr>
                <td><span class="stop-number">${stop.stopNumber}</span></td>
                <td><strong>${esc(stop.dealerName)}</strong></td>
                <td>${esc(stop.address)}</td>
                <td>${esc(stop.phone)}</td>
                <td>${esc(stop.arrivalTime)}</td>
                <td>${esc(stop.serviceTime)} min</td>
              </tr>
            `
              )
              .join('')}
          </tbody>
        </table>
      </div>

      <div class="footer">
        <p>Generated by Vehix on ${new Date().toLocaleString()}</p>
        <p>
          <strong>Important:</strong>
          Please contact dealers before arrival and confirm recall work completion.
        </p>
      </div>
    </body>
    </html>
  `;
};

export const printRouteSheet = async (route: TechnicianRoute): Promise<void> => {
  const routeData = formatRouteForPrint(route);
  const htmlContent = generateRouteSheetHTML(routeData);

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();

    // Update lifecycle records for all recalls in the route
    try {
      const { updateLifecycleOnPrint } = await import('@/lib/supabase/recallLifecycle');
      const { data: sessionData } = await import('@/lib/supabase/client').then(m =>
        m.supabase.auth.getSession()
      );
      const userId = sessionData.session?.user?.id;

      // Extract unique VIN + campaign combinations from route
      const recallKeys = new Set<string>();
      route.stops.forEach(stop => {
        (stop.recalls ?? []).forEach(recall => {
          recallKeys.add(`${recall.vin}|${recall.campaign_number}`);
        });
      });

      // Update lifecycle for each unique recall
      await Promise.all(
        Array.from(recallKeys).map(key => {
          const [vin = '', campaignNumber = ''] = key.split('|');
          return updateLifecycleOnPrint(vin, campaignNumber, userId);
        })
      );
    } catch (lifecycleError) {
      // eslint-disable-next-line no-console -- intentional error log for print lifecycle
      console.error('Error updating lifecycle on print:', lifecycleError);
    }

    // Wait for content to load, then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 1000);
  }
  // If printWindow is null, popup was blocked - no action needed
};

export const printAllRoutes = async (routes: TechnicianRoute[]): Promise<void> => {
  for (let index = 0; index < routes.length; index++) {
    await new Promise(resolve => setTimeout(resolve, index * 2000)); // Stagger prints by 2 seconds
    const route = routes[index];
    if (route) await printRouteSheet(route);
  }
};

/**
 * Address and phone cells for a Route Summary row. Customer stops show the unit and the
 * customer's name + phone (escaped) instead of a dealer phone.
 */
// eslint-disable-next-line complexity -- field fallbacks across stop shapes
function summaryRowLocation(
  stop: PrintStop,
  contacts: PrintContacts
): { isCustomer: boolean; fullAddress: string; phoneCellHtml: string } {
  const flat = stop as PrintStop & {
    dealer_phone?: string;
    dealer_address?: string;
    dealer_city?: string;
    dealer_region?: string;
    dealer_postal_code?: string;
  };
  const isCustomer = isCustomerPrintStop(stop);
  // Check both locations: stop.dealer.phone (recall stops) and stop.dealer_phone (manual stops)
  const phone = stop.dealer?.phone || flat.dealer_phone || '—';
  const baseStreet = stop.dealer?.address || flat.dealer_address;
  const street =
    isCustomer && stop.address_line2 ? `${baseStreet}, ${stop.address_line2}` : baseStreet;
  const city = stop.dealer?.city || flat.dealer_city;
  const region = stop.dealer?.region || flat.dealer_region;
  const firstRecall = (stop.recalls || [])[0] as RecallData | undefined;
  const postalCode =
    stop.dealer?.postal_code || flat.dealer_postal_code || firstRecall?.dealer_postal_code;
  const stateZip = region && postalCode ? `${region} ${postalCode}` : region || postalCode;
  const fullAddress = [street, city, stateZip].filter(Boolean).join(', ') || '—';
  const phoneCellHtml = isCustomer
    ? customerContactText(printStopContactId(stop), contacts) || '—'
    : esc(phone);
  return { isCustomer, fullAddress, phoneCellHtml };
}

/**
 * Generates the Route Summary page (Page 1) for the dealer recall printout.
 * Shows technician info, route overview metrics, and a table of all stops.
 */
export const generateRouteSummaryPageHTML = (
  route: TechnicianRoute,
  dealerNotes: Record<string, string> = {},
  contacts: PrintContacts = {}
): string => {
  const now = new Date();
  const generatedDate = now.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  const techName = route.technician?.full_name || 'N/A';
  const techPhone = route.technician?.phone_number || '—';
  const techEmail = route.technician?.email || '—';

  // Sort stops by stop_order
  const sortedStops = [...route.stops].sort((a, b) => a.stop_order - b.stop_order);

  // Calculate total recalls across all stops
  const totalRecalls = sortedStops.reduce((sum, stop) => {
    const recalls = stop.recalls || [];
    const uniqueRecalls = new Set(recalls.map(r => `${r.vin}-${r.campaign_number}`));
    return sum + uniqueRecalls.size;
  }, 0);

  // Determine if we need condensed layout (more than 10 stops)
  const isCondensed = sortedStops.length > 10;
  const rowPadding = isCondensed ? '4px 8px' : '8px 12px';
  const fontSize = isCondensed ? '10px' : '11px';

  // Generate stop rows
  const stopRows = sortedStops
    .map((stop, index) => {
      const stopNumber = index + 1;
      const dealerName = stop.dealer?.name || 'Unknown Dealer';
      const { isCustomer, fullAddress, phoneCellHtml } = summaryRowLocation(stop, contacts);

      // Analyze stop content for mixed stop detection
      const analysis = analyzeStop(stop);

      // Build recall text based on stop type
      let recallText: string;
      if (analysis.isPureManualStop) {
        recallText = analysis.manualStopReason
          ? getManualStopReasonLabel(analysis.manualStopReason)
          : 'Manual Stop';
      } else if (analysis.isMixedStop) {
        recallText =
          analysis.recallCount === 1
            ? '1 recall + manual'
            : `${analysis.recallCount} recalls + manual`;
      } else {
        recallText = analysis.recallCount === 1 ? '1 recall' : `${analysis.recallCount} recalls`;
      }

      // Badge styling: dashed for pure manual, solid for recalls and mixed
      const badgeStyle = analysis.isPureManualStop
        ? 'style="border: 2px dashed #333; background: white; color: #333;"'
        : '';

      // Indicator badge: "⌂ CUSTOMER" for a customer's home, "★ MANUAL" for other pure
      // manual stops, "+★" for mixed
      const manualIndicator = isCustomer
        ? ' <span style="font-size: 10px; font-weight: bold; border: 1px dashed #333; ' +
          'padding: 1px 4px; margin-left: 4px;">⌂ CUSTOMER</span>'
        : analysis.isPureManualStop
          ? ' <span style="font-size: 10px; font-weight: bold; border: 1px dashed #333; ' +
            'padding: 1px 4px; margin-left: 4px;">★ MANUAL</span>'
          : analysis.isMixedStop
            ? ' <span style="font-size: 9px; font-weight: bold; border: 1px solid #666; ' +
              'padding: 1px 3px; margin-left: 4px; background: #f0f0f0;">+★</span>'
            : '';

      // Per-dealer technician_notes as a dedicated column (quiet styling
      // so it stays accessible without dominating the table).
      const notesCellContent = renderSummaryNotesCell(stop, dealerNotes);
      const recallsCellStyle = analysis.isPureManualStop
        ? 'font-weight: 600; font-style: italic;'
        : '';

      return `
        <tr>
          <td class="stop-number-cell" rowspan="2">
            <span class="stop-badge" ${badgeStyle}>${stopNumber}</span>
          </td>
          <td class="dealer-name">${esc(isCustomer ? CUSTOMER_STOP_LABEL : dealerName)}${manualIndicator}</td>
          <td class="address-cell">${esc(fullAddress)}</td>
          <td class="notes-cell" rowspan="2">${notesCellContent}</td>
        </tr>
        <tr class="subrow">
          <td class="phone-cell">☎ ${phoneCellHtml}</td>
          <td class="recalls-cell" style="${recallsCellStyle}">${esc(recallText)}</td>
        </tr>
      `;
    })
    .join('');

  return `
    <div class="route-summary-page">
      <div class="summary-header">
        <div class="summary-logo"></div>
        <div class="summary-title-section">
          <div class="summary-main-title">Route Summary</div>
          <div class="summary-date">${generatedDate}</div>
        </div>
      </div>

      <div class="summary-separator"></div>

      <div class="summary-info-row">
        <div class="technician-box">
          <div class="box-title">TECHNICIAN</div>
          <div class="box-content">
            <div><strong>Name:</strong> ${esc(techName)}</div>
            <div><strong>Phone:</strong> ${esc(techPhone)}</div>
            <div><strong>Email:</strong> ${esc(techEmail)}</div>
          </div>
        </div>
        <div class="route-overview-box">
          <div class="box-title">ROUTE OVERVIEW</div>
          <table class="overview-table">
            <tr>
              <td class="overview-label">Total Stops</td>
              <td class="overview-value">${sortedStops.length}</td>
            </tr>
            <tr>
              <td class="overview-label">Total Recalls</td>
              <td class="overview-value">${totalRecalls}</td>
            </tr>
          </table>
        </div>
      </div>

      <div class="stops-section">
        <div class="box-title">ROUTE STOPS</div>
        <table class="stops-table" style="font-size: ${fontSize};">
          <thead>
            <tr>
              <th class="col-number">#</th>
              <th class="col-dealer">DEALER</th>
              <th class="col-address">ADDRESS</th>
              <th class="col-notes">NOTES</th>
            </tr>
          </thead>
          <tbody style="--row-padding: ${rowPadding};">
            ${stopRows}
          </tbody>
        </table>
      </div>

      <div class="notes-section">
        <div class="box-title">NOTES</div>
        <div class="notes-box"></div>
      </div>

    </div>
  `;
};

export const generateDealerRecallSummaryHTML = (
  route: TechnicianRoute,
  dealerNotes: Record<string, string> = {},
  contacts: PrintContacts = {}
): string => {
  const now = new Date();

  const generatedDate = now.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  });

  const techName = route.technician?.full_name || 'N/A';

  // Group stops by dealer (using name-city key for uniqueness)
  const dealerGroups = route.stops.reduce(
    (acc, stop) => {
      // Use name-city key to handle same-named dealers in different cities. Customer stops
      // are all named 'Customer stop', so they get one section per household instead.
      const isCustomer = isCustomerPrintStop(stop);
      const contactId = isCustomer ? printStopContactId(stop) : undefined;
      const dealerKey = isCustomer
        ? `customer:${contactId ?? `${stop.dealer.address}|${stop.address_line2 ?? ''}`}`
        : `${stop.dealer.name}-${stop.dealer.city || ''}`;
      if (!acc[dealerKey]) {
        acc[dealerKey] = {
          dealer: stop.dealer,
          stops: [],
          ...(isCustomer && { isCustomer, contactId, addressLine2: stop.address_line2 })
        };
      }
      acc[dealerKey].stops.push(stop);
      return acc;
    },
    {} as Record<string, DealerGroup>
  );

  // Sort dealers by minimum stop_order to preserve route sequence from single source of truth
  const dealerEntries = Object.entries(dealerGroups).sort((a, b) => {
    const minOrderA = Math.min(...a[1].stops.map(s => s.stop_order || 0));
    const minOrderB = Math.min(...b[1].stops.map(s => s.stop_order || 0));
    return minOrderA - minOrderB;
  });

  // Check for override values (used when printing a single dealer from a larger route)
  const routeAny = route as any;
  const hasOverrides =
    routeAny._stopNumberOverride !== undefined && routeAny._totalStopsOverride !== undefined;
  const totalDealers = hasOverrides ? routeAny._totalStopsOverride : dealerEntries.length;

  // Generate dealer sections (using flatMap to handle mixed stops that need 2 pages)
  const dealerSections = dealerEntries
    .flatMap(([dealerName, dealerGroup], index) => {
      const isFirstDealer = index === 0;
      const dealerNumber = hasOverrides ? routeAny._stopNumberOverride : index + 1;

      // Analyze dealer group for mixed stop detection
      const analysis = analyzeDealerGroup(dealerGroup);

      // Calculate unique recalls count (only for recall-type items)
      const uniqueRecalls = new Map();
      dealerGroup.stops.forEach(stop => {
        (stop.recalls || []).forEach((recall: RecallData) => {
          // Only count recall-type items, not manual stops
          if (!recall.stop_type || recall.stop_type === 'recall') {
            const key = `${recall.vin}-${recall.campaign_number}`;
            if (!uniqueRecalls.has(key)) {
              uniqueRecalls.set(key, recall);
            }
          }
        });
      });
      const totalRecalls = uniqueRecalls.size;

      // Group recalls by VIN
      const recallsByVin = new Map();
      Array.from(uniqueRecalls.values()).forEach(recall => {
        const vin = recall.vin || 'N/A';
        if (!recallsByVin.has(vin)) {
          recallsByVin.set(vin, {
            vin: vin,
            year: recall.car_year || 'N/A',
            make: recall.make_name || 'N/A',
            model: recall.model_name || 'N/A',
            campaigns: []
          });
        }
        recallsByVin.get(vin).campaigns.push({
          number: recall.campaign_number || 'N/A',
          description: recall.recall_title || ''
        });
      });

      // Build dealer address - check multiple sources for postal code
      const dealerAddress = (() => {
        const firstStop = dealerGroup.stops[0];
        const firstRecall = firstStop?.recalls?.[0] as RecallData | undefined;

        // Get postal code from any available source
        const zip =
          firstRecall?.dealer_postal_code ||
          firstStop?.dealer?.postal_code ||
          (firstStop as any)?.dealer_postal_code ||
          '';

        // Get address components from recall or dealer
        const street =
          firstRecall?.dealer_street ||
          firstRecall?.dealer_address ||
          firstStop?.dealer?.address ||
          '';
        const city = firstRecall?.dealer_city || firstStop?.dealer?.city || '';
        const state = firstRecall?.dealer_region || firstStop?.dealer?.region || '';

        const streetWithUnit =
          dealerGroup.addressLine2 && street ? `${street}, ${dealerGroup.addressLine2}` : street;
        const addressParts = [streetWithUnit, city, zip ? `${state} ${zip}` : state].filter(
          Boolean
        );
        return addressParts.length > 0 ? addressParts.join(', ') : 'N/A';
      })();

      // Per-dealer technician_notes from the Simple Recall Map detail panel.
      // Rendered as a quiet single-line under the address so the technician
      // can find it without it dominating the page layout.
      // Per-dealer notes are keyed by name, so they never apply to a customer's home.
      const technicianNote = dealerGroup.isCustomer
        ? undefined
        : dealerNotes[dealerGroup.dealer.name];
      const noteLabel = '<span class="page-header-dealer-notes-label">Notes:</span>';
      const displayName = dealerGroup.isCustomer ? CUSTOMER_STOP_LABEL : dealerName;
      // Customer name + phone so the technician can call ahead and greet them by name;
      // the dispatcher's note for this stop. Both escaped.
      const customerText = dealerGroup.isCustomer
        ? customerContactText(dealerGroup.contactId, contacts)
        : '';
      const customerLabel = '<span class="page-header-dealer-notes-label">Customer:</span>';
      const dispatcherLabel =
        '<span class="page-header-dealer-notes-label">Note for technician:</span>';
      const technicianNoteHtml =
        (technicianNote
          ? `<p class="page-header-dealer-notes">${noteLabel} ${escapeHTML(technicianNote)}</p>`
          : '') +
        (customerText
          ? `<p class="page-header-dealer-notes">${customerLabel} ${customerText}</p>`
          : '') +
        dispatcherNotesFor(dealerGroup.stops)
          .map(n => `<p class="page-header-dealer-notes">${dispatcherLabel} ${escapeHTML(n)}</p>`)
          .join('');

      // Track if this is the first page for this dealer (for page breaks)
      let needsPageBreak = !isFirstDealer;

      // Array to hold pages for this dealer (1 for pure stops, 2 for mixed)
      const pages: string[] = [];

      // Generate recall page if there are recalls
      if (analysis.hasRecallStop) {
        pages.push(`
          <div class="dealer-section ${needsPageBreak ? 'page-break' : ''}">
            <div class="dealer-section-body">
            <header class="page-header">
              <div class="page-header-logo" aria-hidden="true"></div>
              <div class="page-header-content">
                <p class="page-header-doc-title">Dealer Recall Summary</p>
                <h1 class="page-header-dealer-name">${esc(displayName)}</h1>
                <p class="page-header-dealer-address">${esc(dealerAddress)}</p>
                ${technicianNoteHtml}
                <p class="page-header-recall-count">
                  <span class="recall-count-badge">${totalRecalls} Recall${totalRecalls !== 1 ? 's' : ''}</span>
                </p>
              </div>
            </header>

            <div class="separator"></div>

            <div class="section-title">Acknowledgment</div>
            <div class="acknowledgment-section">
              <div class="acknowledgment-col ack-col-technician">
                <h4 class="ack-col-title">Technician Acknowledgment</h4>
                <div class="ack-fields">
                  <div class="ack-info-row">
                    <span class="ack-label">Name:</span>
                    <span class="ack-value">${esc(techName)}</span>
                  </div>
                  <div class="ack-info-row">
                    <span class="ack-label">Date:</span>
                    <span class="ack-value">${generatedDate}</span>
                  </div>
                  <div class="ack-field-row">
                    <span class="ack-label">Tech #:</span>
                    <div class="ack-input"></div>
                  </div>
                  <div class="ack-field-row">
                    <span class="ack-label">Signature:</span>
                    <div class="ack-input"></div>
                  </div>
                </div>
              </div>
              <div class="acknowledgment-col ack-col-dealer">
                <h4 class="ack-col-title">Dealer Acknowledgment</h4>
                <div class="ack-fields">
                  <div class="ack-field-row">
                    <span class="ack-label">Signature:</span>
                    <div class="ack-input"></div>
                  </div>
                  <div class="ack-field-row">
                    <span class="ack-label">Date:</span>
                    <div class="ack-input ack-input-short"></div>
                  </div>
                </div>
                <p class="ack-disclaimer">
                  Client authorizes recall/repair work and acknowledges repairs
                  completed as documented on this form.
                </p>
              </div>
            </div>

            <div class="section-title">Recall Details</div>
            <table class="recall-table">
              <colgroup>
                <col style="width: 18%;" />
                <col style="width: 19%;" />
                <col style="width: 18%;" />
                <col style="width: 13%;" />
                <col style="width: 24%;" />
                <col style="width: 8%;" />
              </colgroup>
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>VIN</th>
                  <th>Stock #</th>
                  <th>Campaign</th>
                  <th>Recall Description</th>
                  <th>Mileage</th>
                </tr>
              </thead>
              <tbody>
                ${Array.from(uniqueRecalls.values())
                  .map(recall => {
                    const vehicle =
                      [recall.car_year, recall.make_name, recall.model_name]
                        .filter(Boolean)
                        .join(' ') || 'N/A';
                    return `
                  <tr>
                    <td>${esc(vehicle)}</td>
                    <td>${esc(recall.vin || 'N/A')}</td>
                    <td>${esc(recall.stock_number || '—')}</td>
                    <td>${esc(recall.campaign_number || 'N/A')}</td>
                    <td>${esc(recall.recall_title || 'N/A')}</td>
                    <td style="min-width: 80px;">&nbsp;</td>
                  </tr>
                `;
                  })
                  .join('')}
              </tbody>
            </table>

            <div class="section-title" style="margin-top: 12px;">Technician Comments</div>
            ${Array.from(recallsByVin.values())
              .map(car => {
                const campaignsDisplay = car.campaigns
                  .map((c: { number: string; description: string }) =>
                    c.description ? `${esc(c.number)} (${esc(c.description)})` : esc(c.number)
                  )
                  .join(', ');
                return `
                <div class="vehicle-comments">
                  <div style="font-weight: bold; font-size: 11px; margin-bottom: 4px;">
                    Vehicle: ${esc(car.year)} ${esc(car.make)} ${esc(car.model)} - VIN: ${esc(car.vin)}
                  </div>
                  <div style="font-size: 9px; color: #666; margin-bottom: 4px;">
                    Campaigns: ${campaignsDisplay}
                  </div>
                  <div style="margin-top: 4px;">
                    <div style="font-weight: bold; font-size: 10px; margin-bottom: 3px;">
                      Technician Comments:
                    </div>
                    <textarea class="comments-textarea"></textarea>
                  </div>
                </div>
              `;
              })
              .join('')}

            </div>
            <div class="dealer-footer">
              Stop ${dealerNumber} of ${totalDealers}
            </div>
          </div>
        `);
        needsPageBreak = true; // Next page needs a break
      }

      // Generate manual stop page if there's a manual stop
      if (analysis.hasManualStop) {
        // Only surface campaign/recall info the dispatcher actually entered. Pure
        // manual stops (e.g. "Oil Change") carry a synthetic 'MANUAL'/'MANUAL-…'
        // campaign and a "<reason> @ <dealer>" title fallback — keep those hidden so
        // the table reads cleanly, matching how recall summaries only show real data.
        const syntheticTitleSuffixes = [` @ ${dealerName}`, ` @ ${dealerGroup.dealer.name}`];
        const isRealCampaign = (c: string | undefined) =>
          !!c && c !== 'MANUAL' && !c.startsWith('MANUAL-');
        const isRealRecallTitle = (t: string | undefined) =>
          !!t && !syntheticTitleSuffixes.some(suffix => t.endsWith(suffix));
        const manualCount = analysis.manualStops.length;
        const manualStopCountLabel = `${manualCount} Manual Stop${manualCount !== 1 ? 's' : ''}`;

        pages.push(`
          <div class="dealer-section ${needsPageBreak ? 'page-break' : ''}">
            <div class="dealer-section-body">
            <header class="page-header">
              <div class="page-header-logo" aria-hidden="true"></div>
              <div class="page-header-content">
                <p class="page-header-doc-title">Manual Stop Details</p>
                <h1 class="page-header-dealer-name">${esc(displayName)}</h1>
                <p class="page-header-dealer-address">${esc(dealerAddress)}</p>
                ${technicianNoteHtml}
                <p class="page-header-recall-count">
                  <span class="recall-count-badge">★ ${manualStopCountLabel}</span>
                </p>
              </div>
            </header>

            <div class="separator"></div>

            <div class="section-title">Acknowledgment</div>
            <div class="acknowledgment-section">
              <div class="acknowledgment-col ack-col-technician">
                <h4 class="ack-col-title">Technician Acknowledgment</h4>
                <div class="ack-fields">
                  <div class="ack-info-row">
                    <span class="ack-label">Name:</span>
                    <span class="ack-value">${esc(techName)}</span>
                  </div>
                  <div class="ack-info-row">
                    <span class="ack-label">Date:</span>
                    <span class="ack-value">${generatedDate}</span>
                  </div>
                  <div class="ack-field-row">
                    <span class="ack-label">Tech #:</span>
                    <div class="ack-input"></div>
                  </div>
                  <div class="ack-field-row">
                    <span class="ack-label">Signature:</span>
                    <div class="ack-input"></div>
                  </div>
                </div>
              </div>
              <div class="acknowledgment-col ack-col-dealer">
                <h4 class="ack-col-title">Customer/Location Acknowledgment</h4>
                <div class="ack-fields">
                  <div class="ack-field-row">
                    <span class="ack-label">Signature:</span>
                    <div class="ack-input"></div>
                  </div>
                  <div class="ack-field-row">
                    <span class="ack-label">Date:</span>
                    <div class="ack-input ack-input-short"></div>
                  </div>
                </div>
                <p class="ack-disclaimer">
                  Customer/location representative acknowledges technician visit
                  and work completed as documented.
                </p>
              </div>
            </div>

            <div class="section-title">Stop Details</div>
            <table class="recall-table">
              <colgroup>
                <col style="width: 20%;" />
                <col style="width: 18%;" />
                <col style="width: 13%;" />
                <col style="width: 24%;" />
                <col style="width: 17%;" />
                <col style="width: 8%;" />
              </colgroup>
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>VIN</th>
                  <th>Campaign</th>
                  <th>Recall Description</th>
                  <th>Reason</th>
                  <th>Mileage</th>
                </tr>
              </thead>
              <tbody>
                ${analysis.manualStops
                  .map(stop => {
                    const stopReason = stop.reason
                      ? getManualStopReasonLabel(stop.reason)
                      : 'Manual Stop';
                    const vehicle =
                      [stop.year, stop.make, stop.model].filter(Boolean).join(' ') || 'N/A';
                    const campaign = isRealCampaign(stop.campaign) ? stop.campaign : 'N/A';
                    const recallDescription = isRealRecallTitle(stop.recallTitle)
                      ? stop.recallTitle
                      : 'N/A';
                    return `
                    <tr>
                      <td>${esc(vehicle)}</td>
                      <td>${esc(stop.vin || 'N/A')}</td>
                      <td>${esc(campaign)}</td>
                      <td>${esc(recallDescription)}</td>
                      <td>${esc(stopReason)}</td>
                      <td style="min-width: 80px;">&nbsp;</td>
                    </tr>
                  `;
                  })
                  .join('')}
              </tbody>
            </table>

            <div class="section-title" style="margin-top: 12px;">Technician Comments</div>
            ${analysis.manualStops
              .map(stop => {
                const stopReason = stop.reason
                  ? getManualStopReasonLabel(stop.reason)
                  : 'Manual Stop';
                const campaignText = isRealCampaign(stop.campaign)
                  ? ` | Campaign: ${esc(stop.campaign)}`
                  : '';
                const recallText = isRealRecallTitle(stop.recallTitle)
                  ? ` | Recall: ${esc(stop.recallTitle)}`
                  : '';
                const notesText = stop.notes ? ` | Notes: ${esc(stop.notes)}` : '';
                return `
                <div class="vehicle-comments">
                  <div style="font-weight: bold; font-size: 11px; margin-bottom: 4px;">
                    ${
                      stop.year && stop.make && stop.model
                        ? `Vehicle: ${esc([stop.year, stop.make, stop.model].join(' '))}` +
                          ` - VIN: ${esc(stop.vin || '—')}`
                        : `Stop: ${esc(displayName)}${stop.vin ? ` - VIN: ${esc(stop.vin)}` : ''}`
                    }
                  </div>
                  <div style="font-size: 9px; color: #666; margin-bottom: 4px;">
                    Reason: ${esc(stopReason)}${campaignText}${recallText}${notesText}
                  </div>
                  <div style="margin-top: 4px;">
                    <div style="font-weight: bold; font-size: 10px; margin-bottom: 3px;">
                      Technician Comments:
                    </div>
                    <textarea class="comments-textarea"></textarea>
                  </div>
                </div>
              `;
              })
              .join('')}

            </div>
            <div class="dealer-footer">
              ★ Manual Stop ${analysis.isMixedStop ? 'at ' : ''}Stop ${dealerNumber} of ${totalDealers} ★
            </div>
          </div>
        `);
      }

      return pages;
    })
    .join('');

  const footerTech = cssStringEscape(techName);
  // Static text only (no user data) — safe inside the CSS string.
  const footerNote = route.stops.some(isCustomerPrintStop)
    ? 'This summary was generated by Vehix · Contains customer information - dispose of securely'
    : 'This summary was generated by Vehix';
  const footerDate = cssStringEscape(generatedDate);

  // Return single HTML document
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Dealer Recall Summary</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          margin: 0;
          padding: 20px;
          color: #1a1a1a;
          font-size: 12px;
          line-height: 1.45;
        }
        @media print {
          body { padding: 12px; }
        }
        .dealer-section {
          position: relative;
          display: flex;
          flex-direction: column;
          padding-bottom: 0;
        }
        .dealer-section-body {
          flex: 1 1 auto;
          min-height: 0;
        }
        .page-header {
          display: flex;
          align-items: flex-start;
          gap: 24px;
          margin-bottom: 0;
          padding-bottom: 8px;
        }
        .page-header-logo {
          flex-shrink: 0;
          width: 120px;
          height: 120px;
          background-color: white;
          background-image: url('/vehix-emblem.png');
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
        }
        .page-header-content {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 6px;
          justify-content: center;
        }
        .page-header-doc-title {
          margin: 0;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: #6b7280;
          line-height: 1.3;
        }
        .page-header-dealer-name {
          margin: 0;
          font-size: 20px;
          font-weight: 700;
          line-height: 1.3;
          color: #1a1a1a;
          letter-spacing: -0.01em;
          overflow-wrap: break-word;
          word-wrap: break-word;
        }
        .page-header-dealer-address {
          margin: 0;
          font-size: 13px;
          line-height: 1.4;
          color: #374151;
          overflow-wrap: break-word;
          word-wrap: break-word;
        }
        .page-header-dealer-notes {
          margin: 2px 0 0 0;
          font-size: 12px;
          line-height: 1.4;
          color: #374151;
          white-space: pre-wrap;
          overflow-wrap: break-word;
          word-wrap: break-word;
        }
        .page-header-dealer-notes-label {
          font-weight: 600;
          color: #1a1a1a;
        }
        .page-header-recall-count {
          margin: 0;
          font-size: 13px;
          line-height: 1.4;
          color: #374151;
        }
        .recall-count-badge {
          display: inline-block;
          padding: 4px 10px;
          font-weight: 600;
          font-size: 12px;
          color: #1a1a1a;
          background: #f3f4f6;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
        }
        .separator {
          border-top: 2px solid #1a1a1a;
          margin: 0 0 16px 0;
        }
        .section-title {
          font-size: 13px;
          font-weight: bold;
          margin: 0 0 10px 0;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #1a1a1a;
        }
        .recall-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
          font-size: 11px;
          box-shadow: 0 1px 0 0 #e5e7eb;
        }
        .recall-table th,
        .recall-table td {
          border: 1px solid #d1d5db;
          padding: 8px 10px;
          text-align: left;
          vertical-align: top;
        }
        .recall-table th {
          background: #f3f4f6;
          font-weight: 600;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: #374151;
        }
        .recall-table tbody tr:nth-child(even) { background: #fafafa; }
        .recall-table tbody tr:hover { background: #f5f5f5; }
        .signature-line {
          border-bottom: 1px solid #1a1a1a;
          width: 240px;
          display: inline-block;
        }
        .acknowledgment-section {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 28px;
          margin-bottom: 20px;
          padding: 16px 18px;
          border: 1px solid #1a1a1a;
          page-break-inside: avoid;
          break-inside: avoid;
        }
        .acknowledgment-col {
          min-width: 0;
          display: flex;
          flex-direction: column;
        }
        .ack-col-title {
          margin: 0 0 12px 0;
          font-size: 12px;
          font-weight: bold;
          color: #1a1a1a;
        }
        .ack-fields {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .ack-info-row {
          display: flex;
          align-items: baseline;
          gap: 8px;
          font-size: 11px;
          line-height: 1.5;
        }
        .ack-label {
          flex-shrink: 0;
          width: 70px;
          font-weight: 600;
          color: #374151;
          font-size: 11px;
        }
        .ack-value {
          color: #1a1a1a;
        }
        .ack-field-row {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .ack-field-row .ack-label {
          width: 70px;
        }
        .ack-input {
          flex: 1;
          min-width: 0;
          height: 20px;
          border-bottom: 1px solid #1a1a1a;
          background: transparent;
        }
        .ack-input-short {
          max-width: 160px;
        }
        .ack-disclaimer {
          margin: 14px 0 0 0;
          font-size: 10px;
          line-height: 1.5;
          color: #4b5563;
        }
        .vehicle-comments {
          margin-bottom: 14px;
          border: 1px solid #e5e7eb;
          padding: 12px;
          page-break-inside: avoid;
          break-inside: avoid;
          border-radius: 2px;
          background: #fafafa;
        }
        .comments-textarea {
          width: 100%;
          height: 72px;
          border: 1px solid #d1d5db;
          padding: 8px;
          font-family: Arial, sans-serif;
          font-size: 11px;
          resize: vertical;
          box-sizing: border-box;
          border-radius: 2px;
        }
        .signature-line.short {
          width: 160px;
        }
        .dealer-footer {
          flex-shrink: 0;
          margin-top: auto;
          padding-top: 12px;
          border-top: 1px solid #e5e7eb;
          text-align: center;
          font-size: 11px;
          color: #6b7280;
          page-break-inside: avoid;
          break-inside: avoid;
        }
        .page-break {
          page-break-before: always;
          break-before: page;
        }
        .avoid-break {
          page-break-inside: avoid;
          break-inside: avoid;
        }
        /* No avoid-break on footer: prevents footer pushed to new page */

        /* Route Summary Page Styles */
        .route-summary-page {
          page-break-after: always;
          break-after: page;
          padding-bottom: 20px;
        }
        .summary-header {
          position: relative;
          min-height: 100px;
          margin-bottom: 10px;
        }
        .summary-logo {
          position: absolute;
          left: 0;
          top: 0;
          width: 100px;
          height: 100px;
          background-image: url('/vehix-emblem.png');
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
        }
        .summary-title-section {
          text-align: center;
          padding: 15px 120px 0 120px;
        }
        .summary-main-title {
          font-size: 28px;
          font-weight: bold;
          margin-bottom: 5px;
        }
        .summary-date {
          font-size: 14px;
          color: #666;
        }
        .summary-separator {
          border-top: 1px solid #000;
          margin: 10px 0 15px 0;
        }
        .summary-info-row {
          display: flex;
          gap: 30px;
          margin-bottom: 20px;
        }
        .technician-box,
        .route-overview-box {
          flex: 1;
        }
        .box-title {
          font-size: 12px;
          font-weight: bold;
          color: #333;
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .box-content {
          font-size: 12px;
          line-height: 1.6;
        }
        .box-content div {
          margin-bottom: 4px;
        }
        .overview-table {
          width: 100%;
          border-collapse: collapse;
        }
        .overview-table td {
          padding: 6px 10px;
          border: 1px solid #ddd;
          font-size: 12px;
        }
        .overview-label {
          background-color: #f5f5f5;
          font-weight: 500;
          width: 60%;
        }
        .overview-value {
          text-align: right;
          font-weight: bold;
        }
        .stops-section {
          margin-bottom: 20px;
        }
        .stops-table {
          width: 100%;
          border-collapse: collapse;
        }
        .stops-table th {
          background-color: #f0f0f0;
          border: 1px solid #000;
          padding: 8px 12px;
          text-align: left;
          font-weight: bold;
          font-size: 11px;
        }
        .stops-table td {
          border: 1px solid #ddd;
          border-bottom: none;
          padding: var(--row-padding, 8px 12px);
          vertical-align: top;
        }
        .stops-table .subrow td {
          border-top: none;
          border-bottom: 1px solid #ddd;
          padding-top: 0;
          color: #666;
          font-size: 10px;
        }
        .col-number {
          width: 40px;
          text-align: center;
        }
        .col-dealer {
          width: 28%;
        }
        .col-address {
          width: auto;
        }
        .col-notes {
          width: 25%;
        }
        .stop-number-cell {
          text-align: center;
          vertical-align: middle;
          border-bottom: 1px solid #ddd !important;
        }
        .stop-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          background-color: #3b82f6;
          color: white;
          border-radius: 50%;
          font-weight: bold;
          font-size: 11px;
        }
        /* Manual stop badge uses inline dashed border styles for B&W printing */
        .dealer-name {
          font-weight: bold;
        }
        .phone-cell {
          font-size: 10px;
        }
        .recalls-cell {
          font-size: 10px;
          color: #666;
        }
        .notes-cell {
          font-size: 10px;
          line-height: 1.4;
          color: #374151;
          vertical-align: middle;
          white-space: pre-wrap;
          overflow-wrap: break-word;
          word-wrap: break-word;
          border-bottom: 1px solid #ddd !important;
        }
        .notes-cell-empty {
          color: #9ca3af;
        }
        .notes-section {
          margin-top: 15px;
        }
        .notes-box {
          border: 1px solid #ccc;
          min-height: 80px;
          background-color: #fafafa;
        }
        @page {
          margin: 0.4in;
          margin-bottom: 0.6in;
          @bottom-left {
            content: "${footerNote}";
            font-size: 8px;
            color: #999;
          }
          @bottom-right {
            content: "Tech: ${footerTech} | Route Date: ${footerDate} | Page " counter(page);
            font-size: 9px;
            color: #333;
          }
        }
        @media print {
          body { margin: 0; padding: 10px; }
          .dealer-footer {
            page-break-before: avoid !important;
            break-before: avoid !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          .page-header-logo,
          .summary-logo {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .route-summary-page {
            page-break-after: always !important;
            break-after: page !important;
          }
          .stop-badge {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .page-break {
            page-break-before: always !important;
            break-before: page !important;
          }
          .avoid-break {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          .recall-table tr {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .vehicle-comments {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          .technician-details {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          .acknowledgment-section {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
        }
      </style>
    </head>
    <body>
      ${hasOverrides ? '' : generateRouteSummaryPageHTML(route, dealerNotes, contacts)}
      ${dealerSections}
    </body>
    </html>
  `;
};

export const printDealerRecallSummary = async (
  route: TechnicianRoute,
  dealerNotes?: Record<string, string>
): Promise<void> => {
  // Auto-fetch per-dealer technician_notes from user_preferences when the caller
  // does not provide an override. Centralized here so all 5 print call sites
  // (PrintRouteDialog, RoutePreviewPage per-dealer/per-tech/print-all) get the
  // feature without duplicating fetch logic.
  // Customer contacts (name + phone for technicians) are fetched alongside, logged as 'print'.
  const [resolvedNotes, contacts] = await Promise.all([
    dealerNotes ?? fetchTechnicianNotesForRoute(route),
    fetchContactsForPrint([route])
  ]);
  const html = generateDealerRecallSummaryHTML(route, resolvedNotes, contacts);

  // Update lifecycle records for all recalls in the route
  try {
    const { updateLifecycleOnPrint } = await import('@/lib/supabase/recallLifecycle');
    const { data: sessionData } = await import('@/lib/supabase/client').then(m =>
      m.supabase.auth.getSession()
    );
    const userId = sessionData.session?.user?.id;

    // Extract unique VIN + campaign combinations from route
    const recallKeys = new Set<string>();
    route.stops.forEach(stop => {
      (stop.recalls ?? []).forEach(recall => {
        recallKeys.add(`${recall.vin}|${recall.campaign_number}`);
      });
    });

    // Update lifecycle for each unique recall
    await Promise.all(
      Array.from(recallKeys).map(key => {
        const [vin = '', campaignNumber = ''] = key.split('|');
        return updateLifecycleOnPrint(vin, campaignNumber, userId);
      })
    );
  } catch (lifecycleError) {
    // eslint-disable-next-line no-console -- intentional error log for print lifecycle
    console.error('Error updating lifecycle on dealer summary print:', lifecycleError);
  }

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();

    // Wait for content to load, then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 1000);
  }
  // If printWindow is null, popup was blocked - no action needed
};

// =============================================================================
// Preview Route Conversion & Multi-Route Printing
// =============================================================================

// Types from useRoutePreview (imported inline to avoid circular deps)
interface PreviewVehicle {
  vin: string;
  year: string;
  make: string;
  model: string;
  recalls: Array<{
    vin: string;
    campaign_number: string;
    recall_title: string;
    skill_level?: number;
    estimated_duration?: number;
    stock_number?: string;
    stop_type?: 'recall' | 'manual';
    manual_stop_reason?: string;
    notes?: string;
    dispatcher_note?: string;
    stop_kind?: 'business' | 'customer';
    contact_id?: string;
  }>;
}

interface PreviewDealerInfo {
  id: string;
  name: string;
  stopKind?: 'business' | 'customer';
  contactId?: string;
  addressLine2?: string;
  address: string;
  city: string;
  state: string;
  postalCode?: string;
  phone?: string;
  stats: {
    vehicles: number;
    recalls: number;
    estimatedTime: number;
  };
  vehicles: PreviewVehicle[];
}

export interface PreviewTechnicianRoute {
  id: string;
  technicianId: string;
  technicianName: string;
  routeDate: string;
  status: string;
  dealers: PreviewDealerInfo[];
  stats: {
    totalDealers: number;
    totalRecalls: number;
    totalVehicles: number;
    estimatedDuration: number;
    distanceMiles: number;
  };
  confirmedAt: string | null;
  routeSequence: number;
}

/** Stop-level customer fields carried from the preview into the print route. */
const previewCustomerFields = (dealer: PreviewDealerInfo) => ({
  ...(dealer.stopKind && { stop_kind: dealer.stopKind }),
  ...(dealer.contactId && { contact_id: dealer.contactId }),
  ...(dealer.addressLine2 && { address_line2: dealer.addressLine2 })
});

/**
 * Convert useRoutePreview TechnicianRoute to printRoutes TechnicianRoute format
 * This ensures the preview page uses the same print format as /routing/manual
 */
export function convertPreviewRouteToPrintRoute(
  previewRoute: PreviewTechnicianRoute
): TechnicianRoute {
  return {
    technician_id: previewRoute.technicianId,
    technician: {
      id: previewRoute.technicianId,
      full_name: previewRoute.technicianName,
      skill_level: 1,
      phone_number: '',
      email: ''
    },
    stops: previewRoute.dealers.map((dealer, index) => ({
      id: dealer.id,
      dealer: {
        id: dealer.id,
        name: dealer.name,
        address: dealer.address,
        city: dealer.city,
        region: dealer.state,
        postal_code: dealer.postalCode || '',
        phone: dealer.phone || '',
        latitude: 0,
        longitude: 0,
        recall_count: dealer.stats.recalls,
        skill_level: 1
      },
      arrival_time: '',
      departure_time: '',
      service_duration: dealer.stats.estimatedTime,
      stop_order: index + 1,
      recalls: dealer.vehicles.flatMap(vehicle =>
        vehicle.recalls.map(recall => ({
          vin: recall.vin,
          campaign_number: recall.campaign_number,
          recall_title: recall.recall_title,
          car_year: vehicle.year,
          make_name: vehicle.make,
          model_name: vehicle.model,
          ...(recall.stock_number && { stock_number: recall.stock_number }),
          dealer_address: dealer.address,
          dealer_city: dealer.city,
          dealer_region: dealer.state,
          ...(dealer.postalCode && { dealer_postal_code: dealer.postalCode }),
          ...(recall.stop_type && { stop_type: recall.stop_type }),
          ...(recall.manual_stop_reason && { manual_stop_reason: recall.manual_stop_reason }),
          ...(recall.stop_kind && { stop_kind: recall.stop_kind }),
          ...(recall.contact_id && { contact_id: recall.contact_id }),
          ...(recall.dispatcher_note && { dispatcher_note: recall.dispatcher_note })
        }))
      ),
      ...previewCustomerFields(dealer),
      ...(dealer.vehicles[0]?.recalls[0]?.stop_type && {
        stop_type: dealer.vehicles[0].recalls[0].stop_type
      })
    })),
    total_distance: previewRoute.stats.distanceMiles,
    total_duration: previewRoute.stats.estimatedDuration,
    route_color: '#3b82f6'
  };
}

/**
 * Convert a single PreviewDealerInfo to a print-ready TechnicianRoute
 * Used for printing individual dealer cards
 */
export function convertPreviewDealerToPrintRoute(
  dealer: PreviewDealerInfo,
  technicianName?: string
): TechnicianRoute {
  return {
    technician_id: 'single-dealer',
    technician: {
      id: 'single-dealer',
      full_name: technicianName || '',
      skill_level: 1,
      phone_number: '',
      email: ''
    },
    stops: [
      {
        id: dealer.id,
        dealer: {
          id: dealer.id,
          name: dealer.name,
          address: dealer.address,
          city: dealer.city,
          region: dealer.state,
          postal_code: dealer.postalCode || '',
          phone: dealer.phone || '',
          latitude: 0,
          longitude: 0,
          recall_count: dealer.stats.recalls,
          skill_level: 1
        },
        arrival_time: '',
        departure_time: '',
        service_duration: dealer.stats.estimatedTime,
        stop_order: 1,
        recalls: dealer.vehicles.flatMap(vehicle =>
          vehicle.recalls.map(recall => ({
            vin: recall.vin,
            campaign_number: recall.campaign_number,
            recall_title: recall.recall_title,
            car_year: vehicle.year,
            make_name: vehicle.make,
            model_name: vehicle.model,
            ...(recall.stock_number && { stock_number: recall.stock_number }),
            dealer_address: dealer.address,
            dealer_city: dealer.city,
            dealer_region: dealer.state,
            ...(dealer.postalCode && { dealer_postal_code: dealer.postalCode }),
            ...(recall.stop_type && { stop_type: recall.stop_type }),
            ...(recall.manual_stop_reason && { manual_stop_reason: recall.manual_stop_reason }),
            ...(recall.stop_kind && { stop_kind: recall.stop_kind }),
            ...(recall.contact_id && { contact_id: recall.contact_id }),
            ...(recall.dispatcher_note && { dispatcher_note: recall.dispatcher_note })
          }))
        ),
        ...previewCustomerFields(dealer),
        ...(dealer.vehicles[0]?.recalls[0]?.stop_type && {
          stop_type: dealer.vehicles[0].recalls[0].stop_type
        })
      }
    ],
    total_distance: 0,
    total_duration: dealer.stats.estimatedTime,
    route_color: '#3b82f6'
  };
}

/**
 * Print multiple technician routes with page breaks between them
 * Used for "Print All" functionality on route preview page
 */
export async function printMultipleTechnicianRoutes(
  previewRoutes: PreviewTechnicianRoute[]
): Promise<void> {
  if (previewRoutes.length === 0) return;

  // Convert all routes to print format
  const printRoutes = previewRoutes.map(convertPreviewRouteToPrintRoute);
  const firstRoute = printRoutes[0];
  if (!firstRoute) return;

  // One batched fetch shared across every route in the print job.
  const [dealerNotes, contacts] = await Promise.all([
    fetchTechnicianNotesForRoutes(printRoutes),
    fetchContactsForPrint(printRoutes)
  ]);

  // Generate HTML for first route (includes full document structure)
  const firstHtml = generateDealerRecallSummaryHTML(firstRoute, dealerNotes, contacts);

  if (printRoutes.length === 1) {
    // Single route - just print it directly
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(firstHtml);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 1000);
    }
    return;
  }

  // Multiple routes - combine with page breaks
  // Extract body content from each route's HTML
  const extractBodyContent = (html: string): string => {
    const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    return bodyMatch?.[1] ?? '';
  };

  // Extract head content (styles) from first route
  const headMatch = firstHtml.match(/<head[^>]*>([\s\S]*)<\/head>/i);
  const headContent: string = headMatch?.[1] ?? '';

  // Build combined body with page breaks
  const bodyParts = printRoutes.map((route, index) => {
    const html = generateDealerRecallSummaryHTML(route, dealerNotes, contacts);
    let bodyContent = extractBodyContent(html);

    // Add page break before all routes except the first
    if (index > 0) {
      bodyContent = bodyContent.replace(
        /<div class="route-summary/,
        '<div class="route-summary page-break'
      );
    }

    return bodyContent;
  });

  const combinedHtml = `
    <!DOCTYPE html>
    <html>
    <head>
      ${headContent}
    </head>
    <body>
      ${bodyParts.join('\n')}
    </body>
    </html>
  `;

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(combinedHtml);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 1000);
  }
}
